/**
 * Blog post manifest — single source of truth for the blog index, related
 * posts, sitemap, and any "blog post badges" that appear on vertical pages.
 *
 * To add a post:
 *   1. Drop a new file in pages/blog/{slug}.vue using BlogPostShell
 *   2. Add an entry here matching the slug
 *
 * Slugs are deliberately keyword-rich for SEO. Don't shorten them.
 */
export interface BlogPostMeta {
  slug: string
  title: string
  excerpt: string
  vertical: 'migration-agencies' | 'education-agencies' | 'law-firms' | 'real-estate-agencies'
  read_minutes: number
  published_at: string  // YYYY-MM-DD
  author: string
  /** Used for the meta description tag */
  seo_description?: string
  /** Soft accent for the hero (matches the vertical's landing-page tone) */
  accent_from: string
  accent_to: string
}

export const BLOG_POSTS: BlogPostMeta[] = [
  {
    slug: 'law-firm-conflict-checks-trust-accounting-small-practice',
    title: 'Conflict checks and trust accounting for small law firms (without the enterprise price tag)',
    excerpt: 'The two operational risks that sink small practices — a missed conflict and a messy trust ledger — and the lightweight system that closes both.',
    vertical: 'law-firms',
    read_minutes: 7,
    published_at: '2026-07-05',
    author: 'The Caseflow team',
    seo_description: 'How small law firms run conflict-of-interest checks and client-money trust ledgers without enterprise practice-management software.',
    accent_from: 'from-slate-600',
    accent_to: 'to-indigo-600',
  }
]

export const postsForVertical = (vertical: BlogPostMeta['vertical']) =>
  BLOG_POSTS.filter(p => p.vertical === vertical)

export const findPost = (slug: string) =>
  BLOG_POSTS.find(p => p.slug === slug)

export const relatedPosts = (slug: string, vertical: BlogPostMeta['vertical'], n = 3) => {
  // Prefer same-vertical posts, then fall back to recent posts from other verticals.
  const sameV = BLOG_POSTS.filter(p => p.vertical === vertical && p.slug !== slug)
  const otherV = BLOG_POSTS.filter(p => p.vertical !== vertical && p.slug !== slug)
  return [...sameV, ...otherV].slice(0, n)
}

export const VERTICAL_LABELS: Record<BlogPostMeta['vertical'], string> = {
  'migration-agencies': 'Migration agencies',
  'education-agencies': 'Education agencies',
  'law-firms': 'Law firms',
  'real-estate-agencies': 'Real estate agencies',
}
